import { NotFoundScreen } from '@/screens/404Screen'
import { BillingScreen } from '@/screens/BillingScreen/BillingScreen'
import { ChangePasswordScreen } from '@/screens/ChangePasswordScreen'
import { ChannelRegistrationScreen } from '@/screens/ChannelRegistrationScreen/ChannelRegistrationScreen'
import { SignupChannelSelectionScreen } from '@/screens/ChannelRegistrationScreen/SignupChannelSelectionScreen'
import { ContentModuleFormScreen } from '@/screens/ContentModuleFormScreen/ContentModuleFormScreen'
import { ContentModuleScreen } from '@/screens/ContentModuleScreen/ContentModuleScreen'
import { ContentModulesScreen } from '@/screens/ContentModulesScreen'
import { FeedbackScreen } from '@/screens/FeedbackScreen'
import { ForgotPasswordScreen } from '@/screens/ForgotPasswordScreen'
import { HelpScreen } from '@/screens/HelpScreen'
import { LoginScreen } from '@/screens/LoginScreen'
import { MetricsScreen } from '@/screens/MetricsScreen'
import { OnboardingScreen } from '@/screens/OnboardingScreen'
import { PeopleScreen } from '@/screens/PeopleScreen'
import { ResetPasswordScreen } from '@/screens/ResetPasswordScreen'
import { SettingsScreen } from '@/screens/SetupScreen/SettingsScreen'
import { SidekicksScreen } from '@/screens/SidekicksScreen'
import { SponsorSelectionScreen } from '@/screens/SponsorSelectionScreen'
import type { StringWithAutocomplete } from '@/types/helpers'
import type {
  RouteDef,
  RouteGateContext,
  RouteRedirectResponse,
  RouteRenderConditionResponse,
} from '@/utils/navigation'
import { getRoutePath } from '@/utils/navigation'

export const routes = {
  home: {
    path: '/',
    renderConditionFn: homeRedirect,
    routes: {
      onboarding: {
        path: '/onboarding',
        component: OnboardingScreen,
        renderConditionFn: onboardingRenderCondition,
        routes: {
          signupChannelSelection: { path: '/signup', component: SignupChannelSelectionScreen },
          channelRegistration: { path: '/:channelId', component: ChannelRegistrationScreen },
        },
      },
      auth: {
        path: '/auth',
        renderConditionFn: authRenderCondition,
        redirectFn: authRedirect,
        routes: {
          login: { path: '/login', component: LoginScreen },
          sponsorSelection: {
            path: '/select-sponsor',
            component: SponsorSelectionScreen,
            renderConditionFn: sponsorSelectionRenderCondition,
          },
          changePassword: { path: '/change-password', component: ChangePasswordScreen },
          forgotPassword: { path: '/forgot-password', component: ForgotPasswordScreen },
          resetPassword: { path: '/reset-password', component: ResetPasswordScreen },
          signup: { path: '/signup' },
        },
      },
      dashboard: {
        path: '/dashboard',
        renderConditionFn: dashboardRenderCondition,
        redirectFn: disabledPagesRedirect,
        routes: {
          gettingStarted: { path: '/getting-started', redirectFn: disabledPagesRedirect },
          sidekicks: {
            path: '/expert-sidekicks',
            component: SidekicksScreen,
            renderConditionFn: sponsorRenderCondition,
            routes: {
              sidekicksForm: { path: '/:id', component: NotFoundScreen },
            },
          },
          metrics: { path: '/metrics', component: MetricsScreen },
          contentModules: {
            path: '/modules',
            component: ContentModulesScreen,
            routes: {
              contentModuleFormEdit: { path: '/:id/edit', component: ContentModuleFormScreen },
              contentModuleFormNew: { path: '/new', component: ContentModuleFormScreen },
              contentModule: { path: '/:id', component: ContentModuleScreen },
            },
          },
          help: { path: '/help', component: HelpScreen },
          billing: { path: '/billing', component: BillingScreen, renderConditionFn: sponsorRenderCondition },

          people: {
            path: '/people',
            component: PeopleScreen,
            renderConditionFn: sponsorRenderCondition,
          },
          feedback: {
            path: '/feedback',
            component: FeedbackScreen,
          },
          settings: {
            path: '/settings',
            component: SettingsScreen,
            renderConditionFn: sponsorRenderCondition,
          },
        },
      },
    },
  },
} as const satisfies RouteDef['routes']

export type RouteParams = {
  home: undefined

  onboarding: undefined
  signupChannelSelection: undefined
  channelRegistration: { channelId: string }

  auth: undefined
  login: undefined
  sponsorSelection: undefined
  signup: undefined
  changePassword: undefined
  forgotPassword: undefined
  resetPassword: undefined

  dashboard: undefined
  gettingStarted: undefined
  sidekicks: undefined
  sidekicksForm: { id: StringWithAutocomplete<'new'> }
  contentModules: undefined
  contentModule: { id: string }
  contentModuleFormNew: undefined
  contentModuleFormEdit: { id: string }
  metrics: undefined
  help: undefined
  setup: undefined
  billing: undefined
  people: undefined
  feedback: undefined
  settings: undefined
}

function homeRedirect({ auth }: RouteGateContext): RouteRedirectResponse {
  const { isAuthenticated, ready } = auth

  if (isAuthenticated) {
    // Wait for authentication store to be fully ready (including setup status check)
    if (!ready) {
      return { redirectTo: getRoutePath('auth') } // Stay on current page while loading
    }

    // Users in setup or not both go to dashboard - setup checklist will show automatically
    return { redirectTo: getRoutePath('dashboard') }
  } else {
    return { redirectTo: getRoutePath('auth') }
  }
}

function authRedirect({ auth }: RouteGateContext): RouteRedirectResponse {
  const { ready, isAuthenticated, isInSetup, pendingSponsorSelection } = auth

  // Wait for authentication store to be fully ready
  if (!ready) {
    return { redirectTo: getRoutePath('login') } // Stay on login while loading
  }

  // If not authenticated, stay on login
  if (!isAuthenticated) {
    return { redirectTo: getRoutePath('login') }
  }

  // User is authenticated - check where they should go
  if (pendingSponsorSelection) {
    return { redirectTo: getRoutePath('sponsorSelection') }
  }

  if (isInSetup) {
    return { redirectTo: getRoutePath('contentModules') }
  }
  return { redirectTo: getRoutePath('dashboard') }
}

function sponsorSelectionRenderCondition({ auth }: RouteGateContext): RouteRenderConditionResponse {
  const { isAuthenticated, ready, isInSetup, pendingSponsorSelection } = auth

  // Only render this page if sponsor selection is actually pending
  if (pendingSponsorSelection) {
    return true // Render the sponsor selection screen
  }

  // If no sponsor selection pending, redirect based on auth state
  if (isAuthenticated && ready) {
    if (isInSetup) {
      return { redirectTo: getRoutePath('contentModules') }
    }
    return { redirectTo: getRoutePath('dashboard') }
  }

  // Not authenticated, go to login
  return { redirectTo: getRoutePath('login') }
}

function authRenderCondition({ auth }: RouteGateContext): RouteRenderConditionResponse {
  const { isAuthenticated, ready, isInSetup, pendingSponsorSelection } = auth

  // Wait for authentication store to be fully ready before checking any state
  if (!ready) {
    return true // Stay on current page while loading
  }

  // Not authenticated - allow access to login/auth pages
  if (!isAuthenticated) {
    return true
  }

  // User is authenticated - check where they should go
  if (pendingSponsorSelection) {
    return { redirectTo: getRoutePath('sponsorSelection') }
  }

  if (isInSetup) {
    // Redirect to content modules for setup flow (first step is Create Content)
    return { redirectTo: getRoutePath('contentModules') }
  }

  return { redirectTo: getRoutePath('dashboard') }
}

function dashboardRenderCondition({ auth }: RouteGateContext): RouteRenderConditionResponse {
  const { isAuthenticated } = auth

  return isAuthenticated || { redirectTo: getRoutePath('auth') }
}

function disabledPagesRedirect(): RouteRedirectResponse {
  return { redirectTo: getRoutePath('contentModules') }
}

function sponsorRenderCondition({ auth }: RouteGateContext): RouteRenderConditionResponse {
  const { isAuthenticated, isSponsorAdmin } = auth

  return !isAuthenticated ? { redirectTo: getRoutePath('auth') } : isAuthenticated && isSponsorAdmin
}

function onboardingRenderCondition(): RouteRenderConditionResponse {
  // Allow access to onboarding for all users (authenticated or not)
  return true
}
